var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { EMessageChannel } from "@/types/messaging";
import { reasonText } from "@/services/messagingReasons";
/** Historic synthetic senders keep their entries, shown with the demo labels. */
export var demoLabel = function (value) {
    return value
        .replace(/^Fake(?=[ A-Z])/, "Demo")
        .replace(/fake-(?=provider\.example\.org|wa-number-|waba-|infobip\.example\.org)/g, "demo-")
        .replace(/fake\.(?=elections|staff\.association)/g, "demo.");
};
/** A template parameter as written: `name: value` for a named one. */
export var parameterText = function (_a) {
    var name = _a.name, value = _a.value;
    return name ? "".concat(name, ": ").concat(value) : value;
};
/** Whether an email has HTML: `has_html` in the list, the HTML itself in a full entry. */
export var hasHtml = function (message) { var _a; return !!((_a = message.content.has_html) !== null && _a !== void 0 ? _a : message.content.html); };
/** The template line of a template message: its name and language. */
export var templateLabel = function (content, t) {
    var _a;
    return t("sentMessages.content.template", {
        name: content.template_name,
        language: (_a = content.template_language) !== null && _a !== void 0 ? _a : "",
    });
};
/** Known local demo templates have approved sample copy, unlike arbitrary provider IDs. */
export var demoMessageText = function (content, t) {
    var _a, _b;
    if (content.text)
        return content.text;
    var lng = ((_a = content.template_language) === null || _a === void 0 ? void 0 : _a.replace("_", "-")) || "en";
    if (content.template_name === "step_code" && content.code) {
        return t("sentMessages.content.demoCode", { lng: lng, code: content.code });
    }
    if (content.template_name !== "step_notice")
        return null;
    var parameters = (_b = content.parameters) !== null && _b !== void 0 ? _b : [];
    var _c = parameters.map(function (_a) {
        var value = _a.value;
        return value;
    }), name = _c[0], event = _c[1], url = _c[2];
    if (parameters.length === 3 && name && event && url) {
        return t("sentMessages.content.demoNotice", { lng: lng, name: name, event: event, url: url });
    }
    // Previously seeded reminders carried only the greeting and portal URL.
    if (parameters.length === 2 && name && /^https?:\/\//i.test(event)) {
        return t("sentMessages.content.demoLegacyNotice", { lng: lng, name: name, url: event });
    }
    return null;
};
/** One line for a table cell or a chat list. */
export var contentPreview = function (message, t) {
    var _a;
    var content = message.content;
    if (message.channel === EMessageChannel.EMAIL) {
        return content.subject || t("sentMessages.email.noSubject");
    }
    var messageText = demoMessageText(content, t);
    if (messageText)
        return messageText;
    if (content.template_name) {
        var parameters = ((_a = content.parameters) !== null && _a !== void 0 ? _a : []).map(parameterText).join(", ");
        var template = templateLabel(content, t);
        return parameters ? "".concat(template, ": ").concat(parameters) : template;
    }
    return content.text || t("sentMessages.content.noText");
};
/**
 * The fake provider's reasons (`routes/fake_provider.rs`), verbatim, mapped to
 * their translations; the messaging reasons it shares through `reasonText`,
 * and any other as harvest wrote it.
 */
export var FAKE_PROVIDER_REASONS = {
    "Several accounts use this sender": "SEVERAL_ACCOUNTS",
    "The accounts couldn't be read": "ACCOUNTS_UNREADABLE",
    "The webhook component didn't answer": "UNREACHABLE",
    "Paste the m.me link from the enrollment page": "LINK_UNREADABLE",
    "The link has no ref: copy the whole link": "LINK_WITHOUT_REF",
    "No Messenger account of this organization uses this Page": "NO_PAGE_ACCOUNT",
    "Several Messenger accounts of this organization use this Page": "SEVERAL_PAGE_ACCOUNTS",
};
export var fakeReasonText = function (t, reason) {
    if (!reason)
        return t("sentMessages.report.noReason");
    var key = FAKE_PROVIDER_REASONS[reason];
    return key ? t("sentMessages.reason.".concat(key)) : reasonText(t, reason);
};
var time = function (message) { return Date.parse(message.received_at) || 0; };
/** Newest first, as the list and the inbox show them. */
export var newestFirst = function (messages) {
    return __spreadArray([], messages, true).sort(function (a, b) { return time(b) - time(a); });
};
/**
 * A chat per sender and recipient, as the apps keep one chat per business
 * number (R6-07); the most recent chat first.
 */
export var conversations = function (messages) {
    var _a;
    var chats = new Map();
    for (var _i = 0, messages_1 = messages; _i < messages_1.length; _i++) {
        var message = messages_1[_i];
        var key = JSON.stringify([message.sender, message.recipient]);
        chats.set(key, __spreadArray(__spreadArray([], ((_a = chats.get(key)) !== null && _a !== void 0 ? _a : []), true), [message], false));
    }
    return Array.from(chats, function (_a) {
        var key = _a[0], chat = _a[1];
        var ordered = __spreadArray([], chat, true).sort(function (a, b) { return time(a) - time(b); });
        var latest = ordered[ordered.length - 1];
        return { key: key, sender: latest.sender, recipient: latest.recipient, messages: ordered, latest: latest };
    }).sort(function (a, b) { return time(b.latest) - time(a.latest); });
};
/** Date and time in the user's language; the raw value if it doesn't parse. */
export var formatTime = function (value, language) {
    var date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return value;
    try {
        return date.toLocaleString(language, { dateStyle: "short", timeStyle: "medium" });
    }
    catch (_a) {
        return date.toLocaleString();
    }
};
